import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchMe } from '../../api';
import { getToken } from '../../auth/session';
import {
  getSelectedPeTenantId,
  peCreate,
  peId,
  peLabel,
  peList,
  peRemove,
  setSelectedPeTenantId,
} from './peClient';
import { PeNotice, PePage, PeTable } from './PeShared';

export default function PeTenantsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [name, setName] = useState('');
  const selected = getSelectedPeTenantId();

  async function load() {
    setBusy(true);
    setError('');
    try {
      const list = await peList('tenants');
      setRows(list);
      if (!getSelectedPeTenantId()) {
        const me = await fetchMe(getToken());
        const mapped = me.data?.tenant?.peTenantId ?? me.data?.tenant?.PeTenantId;
        if (mapped) {
          setSelectedPeTenantId(mapped);
        } else if (list[0]) {
          setSelectedPeTenantId(peId(list[0]));
        }
      }
    } catch (err) {
      setError(err.message || 'Could not load PE tenants. Confirm Engine connection.');
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function onCreate(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const created = await peCreate('tenants', {
        identifier: identifier.trim(),
        name: name.trim() || identifier.trim(),
      });
      const id = peId(created) || identifier.trim();
      setSelectedPeTenantId(id);
      setIdentifier('');
      setName('');
      setMessage('Tenant created.');
      await load();
      if (id) navigate(`/app/tenants/${id}`);
    } catch (err) {
      setError(err.message || 'Create tenant failed.');
      setBusy(false);
    }
  }

  async function onDelete(row) {
    const id = peId(row);
    if (!id || !window.confirm(`Delete PE tenant ${peLabel(row)}?`)) return;
    setBusy(true);
    setError('');
    try {
      await peRemove(`tenants/${id}`);
      if (String(getSelectedPeTenantId()) === String(id)) setSelectedPeTenantId('');
      await load();
    } catch (err) {
      setError(err.message || 'Delete failed.');
      setBusy(false);
    }
  }

  return (
    <PePage
      title="Tenants & employees"
      lede="Payroll Engine tenants hold divisions, users, employees, and calendars. Add employees natively on a tenant — Import Rebelity roster is optional. People remains a separate Rebelity roster."
    >
      <PeNotice error={error} busy={busy} message={message} />
      <section className="panel">
        <h2>Tenants</h2>
        <PeTable
          columns={[
            { key: 'identifier', alt: 'Identifier', label: 'Identifier' },
            { key: 'name', alt: 'Name', label: 'Name' },
            {
              key: 'selected',
              label: 'Active',
              render: (row) => (String(peId(row)) === String(selected) ? 'Selected' : '—'),
            },
          ]}
          rows={rows}
          empty="No PE tenants yet. Sync a pay period or create one here."
          onOpen={(row) => {
            const id = peId(row);
            setSelectedPeTenantId(id);
            navigate(`/app/tenants/${id}`);
          }}
          onDelete={onDelete}
        />
      </section>
      <section className="panel">
        <h2>Add tenant</h2>
        <form className="form" onSubmit={onCreate}>
          <label>
            Identifier
            <input value={identifier} onChange={(e) => setIdentifier(e.target.value)} required placeholder="rebelity-merchant-1" />
          </label>
          <label>
            Display name
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Cafe" />
          </label>
          <button className="btn btn-primary" type="submit" disabled={busy}>Create tenant</button>
        </form>
      </section>
    </PePage>
  );
}
